import { useEffect, useRef, useState } from 'react';

// Note: This hook manages touch/swipe UI state only (no localStorage usage)

export const useSwipeBack = (onSwipeBack, threshold = 50, edgeWidth = 20) => {
  const [isSwiping, setIsSwiping] = useState(false);
  const [swipeProgress, setSwipeProgress] = useState(0);
  const touchStartX = useRef(null);
  const touchStartY = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const handleTouchStart = (e) => {
      const touch = e.touches[0];
      // Only start tracking if touch begins near the left edge
      if (touch.clientX <= edgeWidth) {
        touchStartX.current = touch.clientX;
        touchStartY.current = touch.clientY;
        setIsSwiping(true);
      }
    };

    const handleTouchMove = (e) => {
      if (!touchStartX.current || !isSwiping) return;

      const touch = e.touches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = touch.clientY - touchStartY.current;

      // Check if horizontal swipe (not vertical scroll)
      if (Math.abs(deltaX) > Math.abs(deltaY)) {
        e.preventDefault(); // Prevent default scroll
        
        // Calculate progress (0 to 1)
        const progress = Math.min(Math.max(deltaX / threshold, 0), 1);
        setSwipeProgress(progress);

        // Add visual feedback to container if ref is provided
        if (containerRef.current) {
          containerRef.current.style.transform = `translateX(${deltaX * 0.3}px)`;
          containerRef.current.style.opacity = 1 - (progress * 0.2);
        }
      }
    };

    const handleTouchEnd = (e) => {
      if (!touchStartX.current || !isSwiping) return;

      const touch = e.changedTouches[0];
      const deltaX = touch.clientX - touchStartX.current;
      const deltaY = touch.clientY - touchStartY.current;

      // Reset visual feedback
      if (containerRef.current) {
        containerRef.current.style.transform = '';
        containerRef.current.style.opacity = '';
        containerRef.current.style.transition = 'transform 0.3s ease, opacity 0.3s ease';
        setTimeout(() => {
          if (containerRef.current) {
            containerRef.current.style.transition = '';
          }
        }, 300);
      }

      // Trigger swipe back if threshold is met and it's a horizontal swipe
      if (deltaX > threshold && Math.abs(deltaX) > Math.abs(deltaY)) {
        onSwipeBack();
      }

      // Reset
      touchStartX.current = null;
      touchStartY.current = null;
      setIsSwiping(false);
      setSwipeProgress(0);
    };

    // Add listeners
    document.addEventListener('touchstart', handleTouchStart, { passive: false });
    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      document.removeEventListener('touchstart', handleTouchStart);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [onSwipeBack, threshold, edgeWidth, isSwiping]);

  return {
    containerRef,
    isSwiping,
    swipeProgress
  };
};

// Optional: Swipe indicator component
export const SwipeIndicator = ({ progress }) => {
  if (progress === 0) return null;

  return (
    <div 
      className="fixed left-0 top-1/2 -translate-y-1/2 z-50 pointer-events-none"
      style={{
        opacity: progress,
        transform: `translateX(${progress * 20}px) translateY(-50%)`
      }}
    >
      <div className="bg-blue-500 text-white p-2 rounded-r-lg shadow-lg">
        <svg width="20" height="20" viewBox="0 0 20 20" fill="currentColor">
          <path d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" />
        </svg>
      </div>
    </div>
  );
};